import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { misPermisos } from "../../services/permisos";
import {
  anotarDesatendida,
  marcarAlertaVista,
  misAlertasSalida,
  omitirSalidaDeHoy,
} from "../../services/salidaPendiente";
import { adentroAhora, autorizados } from "../../services/vigilancia";
import { diaAr, hoyAr } from "../../shared/fechaAr";
import {
  esDeLaBarrera,
  fase,
  pendientesDeSalida,
  toqueActual,
} from "../../shared/salidaPendiente";
import "./css/SalidaPendiente.css";

/* Registro de Salida Pendiente (Guillermo, Portería — ticket 417).
 *
 * Tres piezas que comparten un estado, por eso un contexto:
 *
 *   · El BANNER amarillo, de 18:30 a 19:00, cada 5 minutos, mientras haya
 *     autos del personal que entraron hoy sin salida registrada. «Aceptar»
 *     lleva a Autos Autorizados (donde se registra la salida) y vuelve al
 *     próximo toque si sigue pendiente; «Omitir» (horas extra) no vuelve hoy.
 *   · La CAMPANA de la cabecera, con asterisco rojo mientras haya alguna
 *     desatendida sin «Entendido», y la lista desplegable.
 *   · El que mira el reloj: cada medio minuto, y sólo pregunta a la base de
 *     lunes a jueves no feriados desde las 18:30.
 *
 * Lo monta `LayoutSwitcher`, así que anda igual con el menú al costado y con
 * la barra de arriba. Las reglas (horarios, feriados, quién) viven en
 * `shared/salidaPendiente.js`. */

const Contexto = createContext(null);

const CADA_MS = 30 * 1000;
const claveCerrado = (mail, dia) => `salida-pendiente:${mail}:${dia}`;

const leerCerrado = (mail, dia) => {
  try {
    // Ojo: Number(null) es 0, que es el primer toque. Sin nada guardado, -1.
    const s = localStorage.getItem(claveCerrado(mail, dia));
    const v = s === null || s === "" ? NaN : Number(s);
    return Number.isFinite(v) && v >= 0 ? v : -1;
  } catch {
    return -1;
  }
};
const guardarCerrado = (mail, dia, toque) => {
  try {
    localStorage.setItem(claveCerrado(mail, dia), String(toque));
  } catch {
    /* modo privado: el banner vuelve al recargar, nada más */
  }
};

export const SalidaPendienteProvider = ({ children, reloj = () => new Date() }) => {
  const { fullUser } = useAuth();
  const mail = String(fullUser?.mail || "").toLowerCase();
  const [permisos, setPermisos] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [ahora, setAhora] = useState(reloj);
  const [cerrado, setCerrado] = useState(-1);
  const relojRef = useRef(reloj);
  relojRef.current = reloj;

  const activo = !!mail && esDeLaBarrera(permisos);
  const dia = hoyAr(ahora);
  const f = fase(ahora);
  const toque = toqueActual(ahora);
  const deHoy = alertas.find((a) => a.dia === dia) || null;

  useEffect(() => {
    if (!mail) return undefined;
    let vivo = true;
    misPermisos()
      .then((p) => vivo && setPermisos(p))
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [mail]);

  const recargarAlertas = useCallback(async () => {
    if (!mail) return;
    try {
      setAlertas(await misAlertasSalida(mail));
    } catch {
      /* sin conexión: queda la última que se leyó */
    }
  }, [mail]);

  useEffect(() => {
    if (activo) recargarAlertas();
  }, [activo, recargarAlertas]);

  // El reloj.
  useEffect(() => {
    if (!activo) return undefined;
    const t = setInterval(() => setAhora(relojRef.current()), CADA_MS);
    return () => clearInterval(t);
  }, [activo]);

  useEffect(() => {
    if (mail) setCerrado(leerCerrado(mail, dia));
  }, [mail, dia]);

  /* Quién sigue adentro. Se pregunta en cada toque nuevo (así, si registró las
   * salidas, el siguiente ya no aparece) y una vez al agotarse. */
  const enVentana = f === "avisos" || f === "agotado";
  const llave = `${dia}|${f}|${toque}`;
  useEffect(() => {
    if (!activo || !enVentana || deHoy) {
      setPendientes([]);
      return undefined;
    }
    let vivo = true;
    (async () => {
      try {
        const [adentro, lista] = await Promise.all([adentroAhora(), autorizados()]);
        const p = pendientesDeSalida(adentro, lista, relojRef.current());
        if (!vivo) return;
        setPendientes(p);
        if (f === "agotado" && p.length) {
          await anotarDesatendida(mail, dia, p);
          if (vivo) await recargarAlertas();
        }
      } catch {
        /* sin conexión: se vuelve a intentar en el próximo toque */
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, llave, !!deHoy]);

  const verBanner =
    activo && f === "avisos" && !deHoy && pendientes.length > 0 && toque > cerrado;

  const aceptar = (navegar) => {
    guardarCerrado(mail, dia, toque);
    setCerrado(toque);
    navegar("/vigilancia");
  };

  const omitir = async () => {
    guardarCerrado(mail, dia, toque);
    setCerrado(toque);
    try {
      await omitirSalidaDeHoy(mail, dia);
    } catch {
      /* si no se pudo guardar, al menos este toque queda cerrado */
    }
    await recargarAlertas();
  };

  const entendido = async (id) => {
    try {
      await marcarAlertaVista(id);
    } catch {
      return;
    }
    await recargarAlertas();
  };

  const desatendidas = activo ? alertas.filter((a) => a.estado === "desatendida") : [];

  return (
    <Contexto.Provider value={{ desatendidas, entendido }}>
      {children}
      {verBanner && (
        <BannerConRuta pendientes={pendientes} onAceptar={aceptar} onOmitir={omitir} />
      )}
    </Contexto.Provider>
  );
};

/* El router se pide recién acá, cuando hay banner: el proveedor envuelve los
 * armazones y se monta también donde no hay router (tests del conmutador). */
const BannerConRuta = ({ onAceptar, ...props }) => {
  const navegar = useNavigate();
  return <BannerSalida {...props} onAceptar={() => onAceptar(navegar)} />;
};

export const BannerSalida = ({ pendientes = [], onAceptar, onOmitir }) => (
  <div className="salida-banner" role="alert">
    <b className="salida-banner-titulo">Registro de Salida Pendiente</b>
    <ul className="salida-banner-lista">
      {pendientes.map((p) => (
        <li key={p.id}>
          {p.patente && <span className="salida-pat">{p.patente}</span>} {p.nombre}
        </li>
      ))}
    </ul>
    <div className="salida-banner-acciones">
      <button type="button" className="salida-btn salida-btn--omitir" onClick={onOmitir}>
        Omitir
      </button>
      <button type="button" className="salida-btn" onClick={onAceptar}>
        Aceptar
      </button>
    </div>
  </div>
);

/* La campana de la cabecera. Sin contexto (tests de la cabecera, pantallas
 * sueltas) o sin alertas desatendidas, no dibuja nada. */
export const CampanaSalidas = ({ className = "hub-boton-icono" }) => {
  const ctx = useContext(Contexto);
  const [abierta, setAbierta] = useState(false);
  const caja = useRef(null);

  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierta(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierta]);

  if (!ctx || !ctx.desatendidas.length) return null;
  const { desatendidas, entendido } = ctx;
  const sinVer = desatendidas.some((a) => !a.visto_en);

  return (
    <div className="salida-campana" ref={caja}>
      <button
        type="button"
        className={className}
        onClick={() => setAbierta((v) => !v)}
        aria-label="Salidas sin registrar"
        aria-expanded={abierta}
        title="Salidas sin registrar"
      >
        <span aria-hidden="true">🔔</span>
        {sinVer && (
          <span className="salida-asterisco" data-testid="asterisco-salida">
            *
          </span>
        )}
      </button>
      {abierta && (
        <div className="salida-menu" role="menu">
          <b className="salida-menu-titulo">Salidas sin registrar</b>
          <ul>
            {desatendidas.map((a) => (
              <li key={a.id} className={a.visto_en ? "visto" : ""}>
                <div className="salida-menu-cab">
                  <span>{diaAr(a.dia)}</span>
                  {!a.visto_en && (
                    <button type="button" className="salida-ok" onClick={() => entendido(a.id)}>
                      Entendido
                    </button>
                  )}
                </div>
                <ul className="salida-menu-autos">
                  {(a.pendientes || []).map((p, i) => (
                    <li key={`${p.patente}-${i}`}>
                      {p.patente && <span className="salida-pat">{p.patente}</span>} {p.nombre}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
